/**
 * Browser Mode Connections Dialog
 *
 * Shows pre-saved demo SQLite databases and supports loading user-uploaded
 * SQLite files. Displays informational messages about browser mode limitations.
 */

import { useRef, useEffect, useState } from 'react';
import { Download, Database, ExternalLink, FolderOpen, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConnectionEngineIcon } from '@/features/connections/components/ConnectionEngineIcon';
import { BROWSER_MODE_DATABASES, type BrowserModeDatabase, type UploadedDatabase } from '../types/browserModeTypes';
import type { Connection } from '@/types/electron';
import { useBrowserModeStore } from '../store/browserModeStore';
import { useConnectionStore } from '@/features/connections/store/connectionStore';
import { cn } from '@/lib/utils';
import { generateUniqueConnectionName, PRODUCT_NAME } from '../browserModeUtils';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import {
  openDatabaseFromBuffer,
  loadDemoDatabase,
  getStorageStatus,
  hasValidExtension,
  checkFileSize,
  validateSqliteHeader,
  readFileAsArrayBuffer,
  generateUploadId,
  cacheUploadedDatabase,
  formatFileSize,
  AUTO_CHECKPOINT_THRESHOLD,
} from '@/web/sqlite';
import { safeServerText } from '@/types/safeConnectionDisplay';

type BrowserModeConnectionsDialogProps = {
  open: boolean;
  onCancel?: () => void;
};

export function BrowserModeConnectionsDialog({ open, onCancel }: BrowserModeConnectionsDialogProps) {
  const {
    loadingDatabaseId,
    loadingProgress,
    uploadedDatabases,
    fileSizeWarning,
    uploadError,
    autoOpenFilePicker,
    startConnecting,
    setConnected,
    setConnectionError,
    setLoadingProgress,
    setPersistenceAvailable,
    addUploadedDatabase,
    setFileSizeWarning,
    setUploadError,
    clearUploadState,
    consumeAutoOpenFilePicker,
    persistenceAvailable,
  } = useBrowserModeStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  // Check storage availability on mount
  useEffect(() => {
    getStorageStatus().then((status) => {
      setPersistenceAvailable(status.persistenceAvailable);
    });
  }, [setPersistenceAvailable]);

  // Clear upload state when dialog opens
  useEffect(() => {
    if (open) {
      clearUploadState();
      setPendingFile(null);
    }
  }, [open, clearUploadState]);

  // Auto-open the file picker when requested (e.g., from StartPage)
  useEffect(() => {
    if (!open || !autoOpenFilePicker) return;

    consumeAutoOpenFilePicker();
    // Delay to ensure the file input is mounted before clicking
    requestAnimationFrame(() => {
      fileInputRef.current?.click();
    });
  }, [open, autoOpenFilePicker, consumeAutoOpenFilePicker]);

  const handleConnect = async (database: BrowserModeDatabase | UploadedDatabase) => {
    startConnecting(database);
    console.log('[BrowserModeConnectionsDialog] Connecting to database:', database.id);

    try {
      let buffer: ArrayBuffer;

      if (database.type === 'uploaded') {
        // For uploaded databases, the data is already in memory
        // This path is only used for switching back to an already-loaded upload
        console.warn('[BrowserModeConnectionsDialog] Reconnecting to uploaded DB not yet supported');
        throw new Error('Please upload the file again');
      } else {
        // Load demo database with progress reporting
        buffer = await loadDemoDatabase(
          database.id,
          database.assetPath,
          (progress) => setLoadingProgress(progress)
        );
      }

      // Open the database from the buffer
      await openDatabaseFromBuffer(database.id, buffer);

      // Mark as connected in browser mode store
      setConnected(database);

      // Generate unique connection name
      const existingNames = useConnectionStore.getState().connections
        .map((c) => (c.alias ? safeServerText(c.alias) : c.database))
        .filter((name): name is string => name !== undefined);
      const connectionName = generateUniqueConnectionName(database.name, existingNames);

      // Create the connection object
      const connection: Connection = {
        id: database.id,
        engine: 'sqlite' as const,
        server: 'browser-demo',
        port: '',
        database: connectionName,
        alias: connectionName,
        authentication: 'No password' as const,
      };

      // Save to connections store (persists to localStorage)
      // Check if connection already exists (reconnecting after page reload)
      const { addConnection, updateConnection, connections, connectToDatabase } = useConnectionStore.getState();
      const existingConnection = connections.find(c => c.id === connection.id);
      if (existingConnection) {
        await updateConnection(connection);
      } else {
        await addConnection(connection);
      }

      // Add to active connections
      await connectToDatabase(connection);

      // Close the dialog - stay on start page instead of opening a new workspace
      onCancel?.();
    } catch (error) {
      console.error('[BrowserModeConnectionsDialog] Connection failed:', error);
      setConnectionError(
        database,
        error instanceof Error ? error.message : 'Failed to connect to database'
      );
    }
  };

  const handleLoadFileClick = () => {
    clearUploadState();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // Reset input so the same file can be selected again

    if (!file) return;

    // Validate extension
    if (!hasValidExtension(file.name)) {
      setUploadError('Please select a valid SQLite file (.sqlite, .db, or .sqlite3)');
      return;
    }

    // Check file size
    const sizeCheck = checkFileSize(file.size);
    if (!sizeCheck.allowed) {
      setUploadError(sizeCheck.error);
      return;
    }

    if (sizeCheck.warning) {
      setFileSizeWarning({ type: 'warning', message: sizeCheck.warning });
      setPendingFile(file);
      return; // Wait for user confirmation
    }

    // No warning, proceed directly
    await processUploadedFile(file);
  };

  const handleConfirmLargeFile = async () => {
    if (pendingFile) {
      await processUploadedFile(pendingFile);
      setPendingFile(null);
      setFileSizeWarning(null);
    }
  };

  const handleCancelLargeFile = () => {
    setPendingFile(null);
    setFileSizeWarning(null);
  };

  const processUploadedFile = async (file: File) => {
    const uploadId = generateUploadId(file.name);

    // Create the uploaded database descriptor
    const uploadedDb: UploadedDatabase = {
      id: uploadId,
      name: file.name,
      description: 'User-uploaded SQLite database',
      assetPath: '',
      tableCount: 0, // Will be discovered after loading
      sizeHint: formatFileSize(file.size),
      type: 'uploaded',
      fileSizeBytes: file.size,
      originalFilename: file.name,
    };

    startConnecting(uploadedDb);
    setLoadingProgress({ phase: 'fetching', progress: 0 });

    try {
      // Read file
      setLoadingProgress({ phase: 'fetching', progress: 30 });
      const buffer = await readFileAsArrayBuffer(file);

      // Validate SQLite header
      setLoadingProgress({ phase: 'parsing', progress: 50 });
      const validation = validateSqliteHeader(buffer);
      if (!validation.valid) {
        setConnectionError(uploadedDb, validation.error);
        return;
      }

      // Open the database
      setLoadingProgress({ phase: 'parsing', progress: 70 });
      await openDatabaseFromBuffer(uploadId, buffer);

      // Get table count from the opened database
      const db = (await import('@/web/sqlite')).getCurrentDatabase();
      let tableCount = 0;
      if (db) {
        try {
          const result = db.exec("SELECT COUNT(*) FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%'");
          if (result[0]?.values?.[0]?.[0] !== undefined) {
            tableCount = Number(result[0].values[0][0]);
          }
        } catch {
          // Ignore errors counting tables
        }
      }

      // Update the descriptor with table count
      uploadedDb.tableCount = tableCount;

      // Cache the database if persistence is available and size allows
      if (persistenceAvailable && file.size <= AUTO_CHECKPOINT_THRESHOLD) {
        setLoadingProgress({ phase: 'caching', progress: 85 });
        await cacheUploadedDatabase(uploadId, buffer);
      }

      // Mark as connected
      setLoadingProgress({ phase: 'ready', progress: 100 });
      setConnected(uploadedDb);
      addUploadedDatabase(uploadedDb);

      // Generate unique connection name
      const existingNames = useConnectionStore.getState().connections
        .map((c) => (c.alias ? safeServerText(c.alias) : c.database))
        .filter((name): name is string => name !== undefined);
      const connectionName = generateUniqueConnectionName(file.name, existingNames);

      // Create the connection object
      const connection: Connection = {
        id: uploadId,
        engine: 'sqlite' as const,
        server: 'browser-upload',
        port: '',
        database: connectionName,
        alias: connectionName,
        authentication: 'No password' as const,
      };

      // Save to connections store (persists to localStorage)
      // Check if connection already exists (reconnecting after page reload)
      const { addConnection, updateConnection, connections, connectToDatabase } = useConnectionStore.getState();
      const existingUploadConnection = connections.find(c => c.id === connection.id);
      if (existingUploadConnection) {
        await updateConnection(connection);
      } else {
        await addConnection(connection);
      }

      // Add to active connections
      await connectToDatabase(connection);

      // Close the dialog - stay on start page instead of opening a new workspace
      onCancel?.();
    } catch (error) {
      console.error('[BrowserModeConnectionsDialog] Upload failed:', error);
      setConnectionError(
        uploadedDb,
        error instanceof Error ? error.message : 'Failed to load database'
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && onCancel) {
          onCancel();
        }
      }}
    >
      <DialogContent className="sm:max-w-[680px] overflow-hidden" data-testid="browser-connections-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="flex items-center gap-2 shrink-0">
              <Database className="h-5 w-5" />
              Connect to Database
            </DialogTitle>
            <p className="text-xs text-muted-foreground text-right">
              Your files stay private. All processing happens locally in your browser.
            </p>
          </div>
        </DialogHeader>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".sqlite,.db,.sqlite3"
          onChange={handleFileChange}
          className="hidden"
          data-testid="sqlite-file-input"
        />

        <div className="space-y-5 overflow-hidden">
          {/* Upload Error */}
          {uploadError && (
            <Alert variant="destructive" data-testid="upload-error">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{uploadError}</AlertDescription>
            </Alert>
          )}

          {/* File Size Warning (with confirmation) */}
          {fileSizeWarning && pendingFile && (
            <Alert className="border-amber-500/50 bg-amber-500/10" data-testid="file-size-warning">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertDescription className="flex items-center justify-between gap-4">
                <span className="text-sm">{fileSizeWarning.message}</span>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={handleCancelLargeFile}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleConfirmLargeFile}
                    data-testid="confirm-large-file"
                  >
                    Continue
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Desktop app CTA */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-center justify-between gap-4">
            <div className="text-sm">
              <p className="font-medium">
                Want the full experience?
              </p>
              <p className="text-muted-foreground">
                SQL Server, PostgreSQL, MySQL/MariaDB, IntelliSense, AI terminal, and more.
              </p>
            </div>
            <Button variant="outline" size="sm" asChild data-testid="download-desktop-cta">
              <a href="https://jamsql.com/?utm_source=app&utm_medium=referral&utm_campaign=connections-dialog#download" target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4 mr-2" />
                Download
              </a>
            </Button>
          </div>

          {/* Load SQLite file button */}
          <Button
            variant="secondary"
            className="w-full"
            onClick={handleLoadFileClick}
            disabled={!!loadingDatabaseId}
            data-testid="load-sqlite-file-button"
          >
            <FolderOpen className="h-4 w-4 mr-2" />
            Load SQLite file
          </Button>

          {/* Scrollable databases container */}
          <div className="overflow-y-auto max-h-[280px] space-y-5 pr-1">
            {/* Recently Loaded Databases - shown first */}
            {uploadedDatabases.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground">Recently Loaded</h3>
                <div className="space-y-2" data-testid="uploaded-databases-list">
                  {uploadedDatabases.map((db) => {
                    const isLoading = loadingDatabaseId === db.id;

                    return (
                      <div
                        key={db.id}
                        data-testid={`uploaded-db-${db.id}`}
                        className="p-3 border rounded-lg border-border hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex justify-between items-center gap-4">
                          <div className="flex gap-3 min-w-0">
                            <ConnectionEngineIcon
                              engine="sqlite"
                              size="md"
                              className="shrink-0"
                            />
                            <div className="min-w-0">
                              <h4 className="font-medium text-sm truncate">{db.name}</h4>
                              <p className="text-xs text-muted-foreground">
                                {db.tableCount} tables • {db.sizeHint}
                              </p>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleConnect(db)}
                            disabled={isLoading || !!loadingDatabaseId}
                            variant="outline"
                            size="sm"
                          >
                            {isLoading ? (
                              <RandomDotmSpinner size={16} />
                            ) : (
                              'Connect'
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Demo Databases Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">Demo Databases</h3>
              <div className="space-y-3" data-testid="browser-databases-list">
                {BROWSER_MODE_DATABASES.map((db) => {
                  const isLoading = loadingDatabaseId === db.id;
                  const showProgress = isLoading && loadingProgress;

                  return (
                    <div
                      key={db.id}
                      data-testid={`browser-db-${db.id}`}
                      className={cn(
                        'p-4 border rounded-lg transition-colors',
                        'border-border hover:bg-muted/50'
                      )}
                    >
                      <div className="flex justify-between items-center gap-4">
                        <div className="flex gap-3 min-w-0">
                          <ConnectionEngineIcon
                            engine="sqlite"
                            size="lg"
                            className="mt-0.5 flex-shrink-0"
                          />
                          <div className="min-w-0">
                            <h4 className="font-semibold flex items-center gap-2">
                              {db.name}
                              <span className="text-[10px] font-normal text-muted-foreground/60">
                                SQLite
                              </span>
                            </h4>
                            <p className="text-sm text-muted-foreground truncate">
                              {db.description}
                            </p>
                            <p className="text-xs text-muted-foreground/70">
                              {db.tableCount} tables • {db.sizeHint}
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleConnect(db)}
                          disabled={isLoading || !!loadingDatabaseId}
                          variant="default"
                          size="sm"
                          className="min-w-[100px] flex-shrink-0"
                          data-testid={`connect-button-${db.id}`}
                        >
                          {isLoading ? (
                            <>
                              <RandomDotmSpinner size={16} className="mr-2" />
                              Loading...
                            </>
                          ) : (
                            'Connect'
                          )}
                        </Button>
                      </div>
                      {showProgress && (
                        <div className="mt-3 space-y-1">
                          <Progress
                            value={loadingProgress.progress}
                            className="h-1"
                            data-testid="loading-progress"
                          />
                          <p className="text-xs text-muted-foreground capitalize">
                            {loadingProgress.phase === 'fetching' && 'Downloading database...'}
                            {loadingProgress.phase === 'parsing' && 'Parsing database...'}
                            {loadingProgress.phase === 'caching' && 'Caching for offline use...'}
                            {loadingProgress.phase === 'ready' && 'Ready!'}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Learn more link */}
          <div className="text-center pt-2 border-t border-border">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              Learn more about {PRODUCT_NAME}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
